import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'react-tooltip';

// FontAwesome.
import { byPrefixAndName } from '@awesome.me/kit-f089350b06/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// React-shared.
import { Checkbox } from '@exonet/react-shared/lib/Form';

// Styled.
import * as S from './styled';

/**
 * A form checkbox with a label and an optional info-icon tooltip carrying the
 * longer description. Shared so every page and modal renders the same control.
 *
 * The tooltip uses `positionStrategy='fixed'` so it is not clipped by a modal's
 * scroll/overflow container (the plain absolute strategy renders it inside that
 * container and it disappears behind the modal chrome).
 *
 * @param {Object} form The react-hook-form object driving the field.
 * @param {string} name The field name to register.
 * @param {string} label The visible label beside the checkbox.
 * @param {string} description Optional longer text, shown in the info tooltip.
 * @param {boolean} disabled If true the checkbox cannot be toggled.
 *
 * @returns {Element} The rendered option.
 */
const CheckboxOption = ({ form, name, label, description = undefined, disabled = false }) => {
  const tooltipId = `option-${name}`;

  return (
    <>
      <Checkbox form={form} name={name} label=" " disabled={disabled}>
        <S.CheckboxLabel>
          <span>{label}</span>
          {description && (
            <S.InfoIcon id={tooltipId}>
              <FontAwesomeIcon icon={byPrefixAndName.fal['circle-info']} />
            </S.InfoIcon>
          )}
        </S.CheckboxLabel>
      </Checkbox>
      {description && (
        <Tooltip
          anchorSelect={`#${tooltipId}`}
          place="bottom-start"
          positionStrategy="fixed"
          clickable
          border="1px solid var(--rt-border-color, transparent)"
          style={{ zIndex: 9999 }}
        >
          {description}
        </Tooltip>
      )}
    </>
  );
};

CheckboxOption.propTypes = {
  form: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  disabled: PropTypes.bool,
};

export default CheckboxOption;
